import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { View, KeyboardAvoidingView, Platform, Pressable, ScrollView } from 'react-native';

import { RESET_PASSWORD } from '../../../common/graphql/auth';
import { useForm } from '../../../common/utils/hooks';
import { useAppContext } from '../../../common/utils/context';
import { getErrorMessage } from '../../../common/utils/auth';
import { AUTH_NAMES } from '../../../common/constants';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import Logo from '../logo/Logo';
import { TextInput } from '../input';
import Text from '../text/Text';
import { PrimaryButton } from '../../design/components';
import { ErrorCallout } from '../callout';
import { createAuthStyles } from './authStyles';


const AuthResetPassword = ({ navigation }) => {
	const [isEmailSent, setIsEmailSent] = useState(false);
	const [error, setError] = useState('');
	const styles = useThemedStyles(createStyles);

	const { values: { username }, onChange } = useForm();
	const [resetPasswordMutation, { loading }] = useMutation(RESET_PASSWORD);
	const { appId } = useAppContext();

	const resetPassword = useCallback(async () => {
		const result = await resetPasswordMutation({ variables: { email: username, appId } })
			.catch(responseError => setError(responseError.graphQLErrors[0]));

		if (result) {
			setIsEmailSent(true);
		}
	}, [resetPasswordMutation, username, setIsEmailSent, appId]);

	return (
		<KeyboardAvoidingView
			style={styles.container}
			behavior={Platform.OS === 'ios' ? 'padding' : undefined}
		>
			<ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
				<View style={styles.form}>
					<View style={styles.logoWrap}>
						<Logo />
					</View>
					{
						isEmailSent ? (
							<Text style={styles.subtitle}>
								A link to reset your password has been sent to <Text style={styles.sentEmail}>{username}</Text>
							</Text>
						) : (
							<>
								<Text style={styles.title}>Reset password</Text>
								<Text style={styles.subtitle}>Enter your email and we will send you a reset link.</Text>
								<TextInput
									autoCapitalize="none"
									autoComplete="email"
									autoCorrect={false}
									keyboardType="email-address"
									onChange={onChange('username')}
									placeholder="Email"
									style={styles.input}
									textContentType="username"
									value={username}
								/>
								{
									Boolean(error) && <ErrorCallout text={getErrorMessage(error, AUTH_NAMES.RESET)} />
								}
								<PrimaryButton
									label={AUTH_NAMES.RESET}
									onPress={resetPassword}
									loading={loading}
									disabled={!username}
									style={styles.button}
								/>
							</>
						)
					}
					<Pressable
						onPress={() => navigation.goBack()}
						accessibilityRole="link"
						style={({ pressed }) => [styles.link, pressed && styles.linkPressed]}
					>
						<Text style={styles.linkText}>Go back</Text>
					</Pressable>
				</View>
			</ScrollView>
		</KeyboardAvoidingView>
	);
};

const createStyles = theme => ({
	...createAuthStyles(theme),
	sentEmail: {
		...theme.ui.type.bodyStrong,
		color: theme.ui.colors.text,
	},
});

export default AuthResetPassword;
